import { createSlice } from "@reduxjs/toolkit";
import { authApi } from "../apis/Authentication/authApi";
import { decodeJwtPayload } from "../utils/decodeJwtPayload";
import { hasAcctAdminRole } from "../utils/authRoles";
import { hasViewRecentUserActivityPrivilege, hasViewAssignedRolePrivileges } from "../utils/authPrivileges";
import {
  getJwtExpiryMs,
  resolveUsernameFromDecoded,
} from "./ssoAuthSlice";

/** Action type from redux-persist (string avoids circular import with persistStore). */
const REHYDRATE = "persist/REHYDRATE";

/** Dev: set to `false` to require real login again. */
export const BYPASS_LOGIN = false;

const initialState = {
  username: null,
  accessToken: null,
  tokenType: null,
  expiresIn: null,
  /** JWT payload claims (decoded from accessToken). */
  decodedToken: null,
  /** Server `expires_in` (seconds) converted to whole minutes. */
  expiresInMinutes: null,
  /** Token expiry as UTC milliseconds (`Date.now()` comparable). From JWT `exp` when present, else `Date.now() + expires_in`. */
  tokenExpiresAtMs: null,
  /** How the session was established: `password` | `sso` | null. */
  authSource: null,
};

function applyTokenResponseToState(state, payload) {
  state.accessToken = payload?.access_token ?? null;
  state.tokenType = payload?.token_type ?? null;
  const rawExpires = payload?.expires_in;
  const expiresIn =
    rawExpires != null && rawExpires !== "" ? Number(rawExpires) : null;
  state.expiresIn =
    expiresIn != null && Number.isFinite(expiresIn) ? expiresIn : null;
  state.expiresInMinutes =
    state.expiresIn != null ? Math.round(state.expiresIn / 60) : null;
  state.decodedToken = decodeJwtPayload(state.accessToken);

  const expSec = state.decodedToken?.exp;
  if (typeof expSec === "number" && Number.isFinite(expSec)) {
    state.tokenExpiresAtMs = expSec * 1000;
  } else if (state.expiresIn != null) {
    state.tokenExpiresAtMs = Date.now() + state.expiresIn * 1000;
  } else {
    state.tokenExpiresAtMs = null;
  }
}

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearAuth: () => ({ ...initialState }),
    /**
     * Apply an SSO JWT into the shared auth session used by API calls and UI.
     * Pair with `ssoAuth/setSsoSession` so both stores stay in sync.
     */
    applySsoToken(state, { payload }) {
      const jwtToken = payload?.jwtToken ?? null;
      const decodedToken =
        payload?.decodedToken ?? (jwtToken ? decodeJwtPayload(jwtToken) : null);
      state.accessToken = jwtToken;
      state.tokenType = "Bearer";
      state.decodedToken = decodedToken;
      state.username = resolveUsernameFromDecoded(decodedToken);
      state.expiresIn = null;
      state.expiresInMinutes = null;
      state.tokenExpiresAtMs = getJwtExpiryMs(decodedToken);
      state.authSource = "sso";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(REHYDRATE, (state, action) => {
        const incoming = action.payload?.auth;
        if (!incoming?.accessToken) {
          return;
        }
        state.username = incoming.username ?? state.username;
        state.accessToken = incoming.accessToken;
        state.tokenType = incoming.tokenType ?? state.tokenType;
        state.expiresIn = incoming.expiresIn ?? state.expiresIn;
        state.expiresInMinutes = incoming.expiresInMinutes ?? state.expiresInMinutes;
        state.tokenExpiresAtMs = incoming.tokenExpiresAtMs ?? state.tokenExpiresAtMs;
        state.authSource = incoming.authSource ?? state.authSource;
        applyTokenResponseToState(state, {
          access_token: incoming.accessToken,
          token_type: incoming.tokenType,
          expires_in: incoming.expiresIn,
        });
        if (!String(state.username ?? "").trim()) {
          state.username = resolveUsernameFromDecoded(state.decodedToken);
        }
      })
      .addMatcher(authApi.endpoints.login.matchFulfilled, (state, { payload, meta }) => {
        const args = meta.arg.originalArgs;
        state.username = args?.username ?? null;
        state.authSource = "password";
        applyTokenResponseToState(state, payload);
      })
      .addMatcher(
        authApi.endpoints.refreshToken.matchFulfilled,
        (state, { payload }) => {
          applyTokenResponseToState(state, payload);
        },
      );
  },
});

export const { clearAuth, applySsoToken } = authSlice.actions;
export const selectAuth = (state) => state.auth;
export const selectIsAuthenticated = (state) =>
  BYPASS_LOGIN || Boolean(state.auth.accessToken);
export const selectIsAcctAdmin = (state) => hasAcctAdminRole(state.auth.decodedToken);
export const selectHasViewRecentUserActivity = (state) =>
  hasViewRecentUserActivityPrivilege(state.auth.decodedToken);
export const selectHasViewAssignedRolePrivileges = (state) =>
  hasViewAssignedRolePrivileges(state.auth.decodedToken);
export default authSlice.reducer;
